import { Injectable, NgZone } from '@angular/core';
import { Observable, Observer } from 'rxjs';

import { ExgMapDataLayerComponent } from '../../exg-map-data-layer/exg-map-data-layer.component';
import { ExgMapGoogleAPIWrapper } from '../exg-map-google-api-wrapper.service';

/**
 * Manages all Data Layers for a Google Map instance.
 */
@Injectable()
export class ExgMapDataLayerManagerService {
  private _layers: Map<ExgMapDataLayerComponent, Promise<google.maps.Data>> =
    new Map<ExgMapDataLayerComponent, Promise<google.maps.Data>>();

  constructor(private _wrapper: ExgMapGoogleAPIWrapper, private _zone: NgZone) { }

  /**
   * Adds a new Data Layer to the map.
   */
  addDataLayer(layer: ExgMapDataLayerComponent): void {
    const newLayer = this._wrapper.createDataLayer({
      style: layer.style,
    } as google.maps.Data.DataOptions)
      .then(d => {
        if (layer.geoJson) {
          // NOTE: accessing "features" on google.maps.Data is undocumented
          this.getDataFeatures(d, layer.geoJson).then(features => (d as any).features = features);
        }
        return d;
      });
    this._layers.set(layer, newLayer);
  }

  deleteDataLayer(layer: ExgMapDataLayerComponent): void {
    // @ts-ignore
    this._layers.get(layer).then(l => {
      l.setMap(null);
      this._layers.delete(layer);
    });
  }

  updateGeoJson(layer: ExgMapDataLayerComponent, geoJson: object | string): void {
    // @ts-ignore
    this._layers.get(layer).then(l => {
      l.forEach(feature => {
        l.remove(feature);

        // NOTE: accessing "features" on google.maps.Data is undocumented
        const index = (l as any).features.indexOf(feature, 0);
        if (index > -1) {
          (l as any).features.splice(index, 1);
        }
      });
      this.getDataFeatures(l, geoJson).then(features => (l as any).features = features);
    });
  }

  setDataOptions(layer: ExgMapDataLayerComponent, options: google.maps.Data.DataOptions): void
  {
    // @ts-ignore
    this._layers.get(layer).then(l => {
      l.setControlPosition(options.controlPosition as any);
      l.setControls(options.controls as any);
      l.setDrawingMode(options.drawingMode as any);
      l.setStyle(options.style as any);
    });
  }

  /**
   * Creates a Google Maps event listener for the given DataLayer as an Observable
   */
  createEventObservable<T>(eventName: string, layer: ExgMapDataLayerComponent): Observable<T> {
    return new Observable((observer: Observer<T>) => {
      // @ts-ignore
      this._layers.get(layer).then((d: google.maps.Data) => {
        d.addListener(eventName, (e: T) => this._zone.run(() => observer.next(e)));
      });
    });
  }

  /**
   * Extract features from a geoJson using google.maps Data Class
   * @param d : google.maps.Data class instance
   * @param geoJson : url or geojson object
   */
  getDataFeatures(d: google.maps.Data, geoJson: object | string): Promise<google.maps.Data.Feature[]> {
    return new Promise<google.maps.Data.Feature[]>((resolve, reject) => {
      if (typeof geoJson === 'object') {
        try {
          const features = d.addGeoJson(geoJson);
          resolve(features);
        } catch (e) {
          reject(e);
        }
      } else if (typeof geoJson === 'string') {
        // @ts-ignore
        d.loadGeoJson(geoJson, null, resolve);
      } else {
        reject(`Impossible to extract features from geoJson: wrong argument type`);
      }
    });
  }
}
